import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

interface SubHeaderBackgroundBandsProps {
  endDate: Dayjs;
  startDate: Dayjs;
  zoomMultiplier: number;
}

const SubHeaderBackgroundBands: React.FC<SubHeaderBackgroundBandsProps> = ({
  startDate,
  endDate,
  zoomMultiplier,
}) => {
  // Generate year markers for background bands (matching header logic)
  const yearMarkers = useMemo(() => {
    const markers: { end: Dayjs; start: Dayjs; year: number }[] = [];
    let currentYear = startDate.year();
    const endYear = endDate.year();

    while (currentYear <= endYear) {
      const yearStart = dayjs().year(currentYear).startOf('year');
      const yearEnd = dayjs().year(currentYear).endOf('year');

      // Clamp to the actual timeline bounds
      const actualStart = yearStart.isBefore(startDate) ? startDate : yearStart;
      const actualEnd = yearEnd.isAfter(endDate) ? endDate : yearEnd;

      markers.push({
        year: currentYear,
        start: actualStart,
        end: actualEnd,
      });

      currentYear += 1;
    }

    return markers;
  }, [startDate, endDate]);

  const totalDays = endDate.diff(startDate, 'days');

  // Today triangle logic
  const todayTriangleData = useMemo(() => {
    const today = dayjs();

    // Only show if today is within the timeline range
    if (today.isBefore(startDate) || today.isAfter(endDate)) {
      return null;
    }

    const daysSinceStart = today.diff(startDate, 'days');
    const leftPercent = (daysSinceStart / totalDays) * 100;
    const formattedDate = today.format('MMMM D, YYYY');

    return {
      leftPercent,
      formattedDate,
    };
  }, [startDate, endDate, totalDays]);

  return (
    <BackgroundContainer zoomMultiplier={zoomMultiplier}>
      {/* Today triangle indicator */}
      {todayTriangleData !== null && (
        <TodayTriangle
          left={todayTriangleData.leftPercent}
          title={todayTriangleData.formattedDate}
        />
      )}

      {/* Year boundary lines */}
      <YearBoundaryLines>
        {yearMarkers.slice(1).map((yearData: { end: Dayjs; start: Dayjs; year: number }) => {
          const daysSinceStart = yearData.start.diff(startDate, 'days');
          const leftPercent = (daysSinceStart / totalDays) * 100;

          return <YearBoundaryLine key={yearData.year} left={leftPercent} />;
        })}
      </YearBoundaryLines>
    </BackgroundContainer>
  );
};

const BackgroundContainer = styled.div<{ zoomMultiplier: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ zoomMultiplier }) => 2000 * zoomMultiplier}px; /* Match ScrollableContent min-width */
  height: 100%;
  pointer-events: none;
  z-index: 10;
`;

const YearBoundaryLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const YearBoundaryLine = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: 0;
  height: 30px; /* Match SubHeaderSpace height */
  width: 2.25px;
  background-image: linear-gradient(#afafaf 33%, rgba(255, 255, 255, 0) 0%);
  background-size: 2.25px 12px;
  background-repeat: repeat-y;
  z-index: 10;
`;

const TodayTriangle = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => `calc(${left}% - 11px)`};
  bottom: -1px; /* Position at bottom of SubHeaderSpace, pointing down */
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 16px solid ${({ theme }) => theme.palette.brand.primary};
  z-index: 21;
  pointer-events: auto;
`;

export { SubHeaderBackgroundBands };
